// Client: input, camera, drawing the page, and the handwritten HUD.
import { Net } from './net.ts';
import { drawPaper, drawPaperFast, setLowDetail, setInkFrame, setWobble, setInkScale, penText, inkLine, inkPath, inkEllipse, inkBlot, INK, RED, PENCIL, PENCIL_SOFT, FONT_PEN } from './ink.ts';
import { spriteCount, setSpriteScale, bake, baked, readMisses, readEvictions, cachePixels, clearSprites, type Baked } from './sprite.ts';
import { drawStatus, drawQuestNote, drawTargetPlate, drawSatchel, drawChat, drawFloat, drawCharacterSheet, type Float, type SatchelHit, type SlotHit, drawMiniMap, questNoteHeight } from './ui.ts';
import { drawProp, drawEnemy, drawLoot, pipBar, hashStr, drawClutterCell, drawDoodleCell, clutterCell, setDoodles } from './art.ts';
import { drawHero, drawNpcFigure, drawQuestMark, figureInk, figureBounds, heroFrameBox, drawFigureFor, drawWalkFrame, setBuild } from './hero.ts';
import type { CharacterState, EntityState, EquipState, ZoneInfo, ZoneProp } from '../../../packages/protocol/src/index.ts';
import { unpackEquip } from '../../../packages/protocol/src/index.ts';
import { QUESTS, CLASSES, ITEMS, moveCollide, solidBox, type Box } from '../../../packages/game-data/src/index.ts';

const $ = (id: string) => document.getElementById(id)!;
const canvas = $('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;

// the page may be served under a sub path (oneblock.cc/ink/), so the socket follows the page
const basePath = location.pathname.replace(/[^/]*$/, '');
const wsUrl = (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + basePath + 'ws';
const isTouch = matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
if (isTouch) document.body.classList.add('touch');
// phones draw the same picture with fewer, bolder strokes
setLowDetail(isTouch || innerWidth < 760);
setWobble(Number(new URLSearchParams(location.search).get('wobble') ?? 0));
// ?off=props,ents,hud,paper — used by scripts/qa-perf.mjs to find what costs what
const OFF = new Set((new URLSearchParams(location.search).get('off') ?? '').split(',').filter(Boolean));
const DEBUG = new URLSearchParams(location.search).has('debug');
// ?build=heroic to look at the taller figure at the size it is actually played at
setBuild(new URLSearchParams(location.search).get('build') === 'chibi' ? 'chibi' : 'heroic');

const floats: Float[] = [];
const mouse = { x: -1, y: -1 };
interface Shot { x0: number; y0: number; x1: number; y1: number; t: number; life: number; kind: 'arrow' | 'bolt' }
const shots: Shot[] = [];
let satchelHits: SatchelHit[] = [];
let heroHits: SlotHit[] = [];
const chatLog: { from: string; ch: string; text: string; t: number }[] = [];

const state = {
  char: null as CharacterState | null,
  zone: null as ZoneInfo | null,
  cam: { x: 700, y: 640 },
  you: '',
  t: 0,
  swing: new Map<string, number>(),   // entity id -> seconds left of a swing animation
  lastAct: new Map<string, number>(),
  target: null as string | null,
  status: 'offline' as 'connecting' | 'online' | 'offline',
  invOpen: false,
  heroOpen: false,
  mapOpen: false,
  questOpen: true,
  chatting: false,
};

const net = new Net(wsUrl, {
  welcome(you, char, zone) {
    state.you = you; state.char = char; state.zone = zone;
    solids = zone.props.filter(p => p.solid).map(solidBox);
    pred.ready = false;
    state.cam.x = zone.spawn.x; state.cam.y = zone.spawn.y;
    $('login').classList.add('gone');
    setTimeout(() => ($('login').style.display = 'none'), 500);
    note(isTouch
      ? `You are on ${zone.title}. Hold your thumb on the left to walk, tap a creature to fight it.`
      : `You are on ${zone.title}. WASD to walk, Space to swing, E to talk or pick up.`, 'info');
  },
  char(char) { state.char = char; },
  chat(from, ch, text) { chatLog.push({ from, ch, text, t: state.t }); if (chatLog.length > 40) chatLog.shift(); },
  note(text, kind) { note(text, kind); },
  hit(src, dst, dmg, crit, kill) {
    const e = net.view().find(x => x.id === dst);
    // a ranged class fires something you can see
    const from = src === state.you ? selfView() : net.view().find(x => x.id === src);
    if (from && e && from.kind === 'player') {
      const eq = unpackEquip(from.eq);
      const wk = eq.weapon ? ITEMS[eq.weapon]?.look?.kind : undefined;
      const kind = wk === 'bow' ? 'arrow' : wk === 'staff' ? 'bolt' : null;
      if (kind && Math.hypot(e.x - from.x, e.y - from.y) > 60) {
        shots.push({ x0: from.x, y0: from.y - 44, x1: e.x, y1: e.y - 20, t: 0, life: 0.16, kind });
      }
    }
    if (e) floats.push({ x: e.x + (Math.random() < 0.5 ? -26 : 26), y: e.y - 96, vx: (Math.random() - 0.5) * 10, text: String(dmg), t: 0, life: 1.1, color: dst === state.you ? RED : INK, big: !!crit });
    state.swing.set(src, 0.28);
    if (kill && e) floats.push({ x: e.x, y: e.y - 118, vx: 0, text: 'down!', t: 0, life: 1.5, color: RED, big: true });
  },
  denied(why) { $('login-error').textContent = why; $('login').classList.remove('gone'); $('login').style.display = ''; },
  status(s) { state.status = s; $('conn').textContent = s === 'online' ? '' : s === 'connecting' ? 'reconnecting…' : 'offline'; },
});

function note(text: string, kind = 'info') {
  const el = document.createElement('div');
  el.className = 'note ' + kind;
  el.textContent = text;
  $('notes').appendChild(el);
  setTimeout(() => el.remove(), 6000);
  while ($('notes').children.length > 4) $('notes').firstChild?.remove();
}

// ---------- login ----------
const savedName = (() => { try { return localStorage.getItem('ink:name') ?? ''; } catch { return ''; } })();
($('name') as HTMLInputElement).value = savedName;
let pickedClass = 'inkblade';
let pickedInk = '#1b3fae';
for (const b of document.querySelectorAll<HTMLButtonElement>('.cls')) {
  b.onclick = () => { pickedClass = b.dataset.cls!; for (const o of document.querySelectorAll('.cls')) o.classList.toggle('on', o === b); drawWho(); };
}
for (const b of document.querySelectorAll<HTMLButtonElement>('.ink')) {
  b.onclick = () => { pickedInk = b.dataset.ink!; for (const o of document.querySelectorAll('.ink')) o.classList.toggle('on', o === b); drawWho(); };
}

// Show the person you are about to play, in the colour you chose, holding what that class holds.
// Reading "Ranger — paper arrows" is not the same as seeing the bow.
const WHO_GEAR: Record<string, EquipState> = {
  inkblade: { weapon: 'pencil_sword', chest: 'pupil_tunic', boots: 'sock_boots' },
  warden: { weapon: 'pencil_sword', offhand: 'eraser_buckler', chest: 'pupil_tunic', boots: 'sock_boots' },
  ranger: { weapon: 'paperclip_bow', chest: 'pupil_tunic', boots: 'sock_boots' },
  scribemage: { weapon: 'ink_staff', chest: 'scribe_robe', head: 'ink_hood' },
};
function drawWho() {
  const cv = document.getElementById('who') as HTMLCanvasElement | null;
  if (!cv) return;
  const dpr = Math.min(3, devicePixelRatio || 1);
  const w = cv.clientWidth || 170, h = cv.clientHeight || 115;
  cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
  const c = cv.getContext('2d');
  if (!c) return;
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, w, h);
  c.save();
  c.translate(w / 2, h - 10);
  const s = Math.min(1.35, (h - 22) / 82);
  c.scale(s, s);
  drawFigureFor(c, WHO_GEAR[pickedClass] ?? WHO_GEAR.inkblade!, 'front', pickedInk);
  c.restore();
}
($('login-form') as HTMLFormElement).onsubmit = (e) => {
  e.preventDefault();
  const name = ($('name') as HTMLInputElement).value.trim();
  const pass = ($('pass') as HTMLInputElement).value;
  $('login-error').textContent = '';
  net.login(name, pass, pickedClass, pickedInk);
};
const savedToken = (() => { try { return localStorage.getItem('ink:token'); } catch { return null; } })();
if (savedToken) net.resume(savedToken);

// ---------- input ----------
const keys = new Set<string>();
addEventListener('keydown', (e) => {
  if (state.chatting) {
    if (e.key === 'Escape') closeChat();
    return;
  }
  if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
  keys.add(e.key.toLowerCase());
  if (e.key === ' ') { e.preventDefault(); attack(); }
  if (e.key.toLowerCase() === 'e') interact();
  if (e.key.toLowerCase() === 'i') setBag(!state.invOpen);
  if (e.key === 'Enter') { e.preventDefault(); openChat(); }
  if (/^[1-9]$/.test(e.key)) useSlot(Number(e.key) - 1);
});
addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));
addEventListener('blur', () => keys.clear());
// Tap anywhere to walk there. Tap a thing to walk up to it and do the obvious thing:
// hit an enemy, talk to an NPC, pick up a bag. This is the whole control scheme on a phone.
// A thumb stick, drawn in pen. It appears where the thumb lands on the left of the screen.
const joy = { on: false, id: -1, ox: 0, oy: 0, x: 0, y: 0 };
const JOY_R = 52;
if (isTouch) {
  canvas.addEventListener('touchstart', (ev) => {
    for (const t of Array.from(ev.changedTouches)) {
      // the open satchel takes the touch first, wherever it is: it covers the joystick half
      if (heroClick(t.clientX, t.clientY)) continue;
      if (satchelClick(t.clientX, t.clientY)) continue;
      if (!joy.on && t.clientX < innerWidth * 0.5) { joy.on = true; joy.id = t.identifier; joy.ox = t.clientX; joy.oy = t.clientY; joy.x = 0; joy.y = 0; }
      else tapWorld(t.clientX, t.clientY);
    }
    ev.preventDefault();
  }, { passive: false });
  canvas.addEventListener('touchmove', (ev) => {
    for (const t of Array.from(ev.changedTouches)) {
      if (t.identifier !== joy.id) continue;
      const dx = t.clientX - joy.ox, dy = t.clientY - joy.oy;
      const d = Math.hypot(dx, dy), k = d > JOY_R ? JOY_R / d : 1;
      joy.x = (dx * k) / JOY_R; joy.y = (dy * k) / JOY_R;
    }
    ev.preventDefault();
  }, { passive: false });
  const endTouch = (ev: TouchEvent) => {
    for (const t of Array.from(ev.changedTouches)) if (t.identifier === joy.id) { joy.on = false; joy.id = -1; joy.x = 0; joy.y = 0; }
  };
  canvas.addEventListener('touchend', endTouch);
  canvas.addEventListener('touchcancel', endTouch);
}

/** a tap: on a creature, go and deal with it; on the ground with a mouse, walk there */
/** a click on a worn slot takes that piece off */
function heroClick(sx: number, sy: number) {
  if (!state.heroOpen) return false;
  const b = heroBox();
  if (sx < b.x || sx > b.x + b.w || sy < b.y || sy > b.y + b.h) return false;
  for (const hit of heroHits) {
    if (sx >= hit.x && sx <= hit.x + hit.w && sy >= hit.y && sy <= hit.y + hit.h) {
      net.send({ t: 'unequip', slot: hit.slot as never });
      return true;
    }
  }
  return true;                       // the sheet swallows the tap either way
}

/** a click inside the open satchel uses that slot instead of the world */
function satchelClick(sx: number, sy: number) {
  if (!state.invOpen) return false;
  const b = satchelBox();
  if (sx < b.x || sy < b.y || sx > b.x + b.w || sy > b.y + b.h) return false;
  for (const hit of satchelHits) {
    if (sx >= hit.x && sx <= hit.x + hit.w && sy >= hit.y && sy <= hit.y + hit.h) { useSlot(hit.slot); break; }
  }
  return true;   // swallow the click either way: the panel is in the way
}

function tapWorld(sx: number, sy: number, allowGround = false) {
  if (heroClick(sx, sy)) return;
  if (satchelClick(sx, sy)) return;
  const p = screenToWorld(sx, sy);
  const hit = net.view()
    .filter(e => (e.kind === 'enemy' || e.kind === 'npc' || e.kind === 'loot') && !e.dead && Math.hypot(e.x - p.x, e.y - p.y) < 52)
    .sort((a, b) => Math.hypot(a.x - p.x, a.y - p.y) - Math.hypot(b.x - p.x, b.y - p.y))[0];
  if (hit) {
    state.target = hit.kind === 'enemy' ? hit.id : null;
    pending = { id: hit.id, kind: hit.kind === 'enemy' ? 'attack' : hit.kind === 'npc' ? 'interact' : 'pickup' };
    if (hit.kind === 'enemy') attack();
    return;
  }
  if (allowGround) { pending = null; state.target = null; autoGoto = { x: p.x, y: p.y }; marker = { x: p.x, y: p.y, t: 0.9 }; }
}

canvas.addEventListener('pointermove', (ev) => { if (ev.pointerType !== 'touch') { mouse.x = ev.clientX; mouse.y = ev.clientY; } });
canvas.addEventListener('pointerdown', (ev) => {
  if (ev.pointerType === 'touch') return;   // handled by the joystick code above
  tapWorld(ev.clientX, ev.clientY, true);
});

function attack() {
  // convenience only: pick the closest living enemy in reach. The server re-checks range and cooldown.
  const me = selfView();
  if (me) {
    const reach = 260;
    const near = net.view()
      .filter(e => e.kind === 'enemy' && !e.dead && Math.hypot(e.x - me.x, e.y - me.y) < reach)
      .sort((a, b) => Math.hypot(a.x - me.x, a.y - me.y) - Math.hypot(b.x - me.x, b.y - me.y))[0];
    if (near) state.target = near.id;
    else if (state.target && !net.view().some(e => e.id === state.target && !e.dead)) state.target = null;
  }
  net.send({ t: 'attack', target: state.target ?? undefined });
  state.swing.set(state.you, 0.28);
}
function interact() {
  const me = selfView(); if (!me) return;
  const near = net.view()
    .filter(e => (e.kind === 'npc' || e.kind === 'loot') && Math.hypot(e.x - me.x, e.y - me.y) < 90)
    .sort((a, b) => Math.hypot(a.x - me.x, a.y - me.y) - Math.hypot(b.x - me.x, b.y - me.y))[0];
  if (!near) return;
  if (near.kind === 'loot') net.send({ t: 'pickup', target: near.id });
  else net.send({ t: 'interact', target: near.id });
}
function useSlot(slot: number) { net.send({ t: 'equip', slot }); }
/** where your character actually is on screen right now (predicted, not the delayed snapshot) */
function selfView(): EntityState | undefined {
  const s = net.view().find(e => e.id === state.you);
  return s && pred.ready ? { ...s, x: pred.x, y: pred.y } : s;
}
function setBag(open: boolean) { if (open) { state.heroOpen = false; markTool('tool-hero', false); document.body.classList.remove('hero-open'); } state.invOpen = open; document.body.classList.toggle('bag-open', open); markTool('tool-bag', open); }

// phone buttons. Touch first, because the click that follows a touch can be swallowed mid-gesture.
function tapBtn(id: string, fn: () => void) {
  const el = document.getElementById(id); if (!el) return;
  el.addEventListener('touchstart', (e) => { e.preventDefault(); el.dataset.touched = '1'; fn(); }, { passive: false });
  el.addEventListener('touchend', (e) => { e.preventDefault(); setTimeout(() => { delete el.dataset.touched; }, 400); }, { passive: false });
  el.addEventListener('click', () => { if (!el.dataset.touched) fn(); });
}
tapBtn('tb-talk', () => interact());
tapBtn('tb-swing', () => attack());
tapBtn('tool-bag', () => setBag(!state.invOpen));
tapBtn('tool-hero', () => { state.heroOpen = !state.heroOpen; markTool('tool-hero', state.heroOpen); document.body.classList.toggle('hero-open', state.heroOpen); if (state.heroOpen) setBag(false); });
tapBtn('tool-map', () => { state.mapOpen = !state.mapOpen; markTool('tool-map', state.mapOpen); });
tapBtn('tool-quests', () => { state.questOpen = !state.questOpen; markTool('tool-quests', state.questOpen); });
tapBtn('tool-settings', () => {
  const on = $('settings').classList.toggle('on');
  markTool('tool-settings', on);
});
function markTool(id: string, on: boolean) { document.getElementById(id)?.classList.toggle('on', on); }
// the settings scrap closes when you tap the page around it
$('settings').addEventListener('click', (e) => {
  if (e.target === $('settings')) { $('settings').classList.remove('on'); markTool('tool-settings', false); }
});
const opt = (id: string, fn: (on: boolean) => void) => {
  const el = document.getElementById(id) as HTMLInputElement | null;
  if (el) el.addEventListener('change', () => fn(el.checked));
};
opt('opt-wobble', (on) => setWobble(on ? 5 : 0));
opt('opt-detail', (on) => { setLowDetail(!on || isTouch || innerWidth < 760); clearSprites(); });
opt('opt-doodles', (on) => { setDoodles(on); clearSprites(); });
($('opt-out') as HTMLButtonElement).onclick = () => {
  try { localStorage.removeItem('ink:token'); localStorage.removeItem('ink:name'); } catch { /* private mode */ }
  location.reload();
};

markTool('tool-quests', true);
tapBtn('tb-chat', () => openChat());

const chatInput = $('chat-input') as HTMLInputElement;
function openChat() { state.chatting = true; $('chat-box').classList.add('on'); chatInput.focus(); }
function closeChat() { state.chatting = false; $('chat-box').classList.remove('on'); chatInput.blur(); }
($('chat-form') as HTMLFormElement).onsubmit = (e) => {
  e.preventDefault();
  const text = chatInput.value.trim();
  if (text) net.send({ t: 'chat', ch: text.startsWith('/w ') ? 'world' : 'local', text: text.replace(/^\/w /, '') });
  chatInput.value = ''; closeChat();
};

// Debug hooks for scripts/qa.mjs. They drive the same intent the keyboard does; the server still
// validates every step, so nothing here can move a character further or faster than walking.
let autoGoto: { x: number; y: number } | null = null;
let camLock: { x: number; y: number } | null = null;   // QA only: look at a place without walking there
// Your own character is simulated here as well as on the server, with the same movement code, so it
// answers the keys instantly instead of arriving 120 ms late in little steps.
const pred = { x: 0, y: 0, a: 0, v: 0, ready: false };
let solids: Box[] = [];
let pending: { id: string; kind: 'attack' | 'interact' | 'pickup' } | null = null;
let marker: { x: number; y: number; t: number } | null = null;
interface InkDebug { __inkGoto?: (x: number, y: number) => void; __ink?: () => unknown; __inkCam?: (x: number, y: number) => void; __inkBagHits?: () => unknown; __inkProf?: () => unknown; __inkFigure?: (eq: unknown, view?: unknown) => unknown }
(window as unknown as InkDebug).__inkGoto = (x, y) => { autoGoto = { x, y }; };
(window as unknown as InkDebug).__inkProf = () => { const out: Record<string, number> = { frames: profN.n, sprites: spriteCount(), misses: readMisses(), evict: readEvictions(), mpx: +(cachePixels() / 1e6).toFixed(1), rs: renderScale, bs: bakeScale(), vis: lastVis }; for (const k in prof) out[k] = +(prof[k]! / Math.max(1, profN.n)).toFixed(2); for (const k in prof) prof[k] = 0; profN.n = 0; return out; };
// A sheet of one kind of prop, every variant side by side, for looking at the art on its own.
// A row of the same character in every grade of gear, so the jump from one tier to the next is
// something you can see rather than something you have to read in a tooltip.
const GEAR_STEPS: { name: string; eq: Record<string, string | undefined> }[] = [
  { name: 'nothing', eq: {} },
  { name: 'pupil', eq: { weapon: 'pencil_sword', chest: 'pupil_tunic', boots: 'sock_boots' } },
  { name: 'ruled', eq: { weapon: 'ruler_blade', offhand: 'eraser_buckler', chest: 'stapled_plate', head: 'paper_cap', boots: 'eraser_boots' } },
  { name: 'marked', eq: { weapon: 'marker_axe', offhand: 'staple_kite', chest: 'stapled_plate', head: 'staple_helm', boots: 'eraser_boots', cape: 'ribbon_cape' } },
  { name: 'bound', eq: { weapon: 'chapter_axe', offhand: 'hardcover_shield', chest: 'binding_plate', head: 'margin_crown', boots: 'courier_boots', cape: 'first_draft_cape' } },
  { name: 'ending', eq: { weapon: 'quill_of_endings', offhand: 'hardcover_shield', chest: 'binding_plate', head: 'margin_crown', boots: 'courier_boots', cape: 'first_draft_cape' } },
];
const CLASS_STEPS: { name: string; eq: Record<string, string | undefined> }[] = [
  { name: 'inkblade', eq: { weapon: 'pencil_sword', chest: 'pupil_tunic' } },
  { name: 'warden', eq: { weapon: 'pencil_sword', offhand: 'eraser_buckler', chest: 'pupil_tunic' } },
  { name: 'ranger', eq: { weapon: 'paperclip_bow', chest: 'pupil_tunic' } },
  { name: 'scribemage', eq: { weapon: 'ink_staff', chest: 'scribe_robe', head: 'ink_hood' } },
  { name: 'mage, kitted', eq: { weapon: 'ink_staff', chest: 'scribe_robe', head: 'ink_hood', cape: 'first_draft_cape', boots: 'courier_boots' } },
  { name: 'ranger, kitted', eq: { weapon: 'paperclip_bow', chest: 'stapled_plate', head: 'staple_helm', boots: 'eraser_boots', cape: 'ribbon_cape' } },
];
let buildSheet = false;
(window as unknown as Record<string, unknown>).__inkBuild = (on: boolean) => { buildSheet = on; };
function drawBuildSheet() {
  const c = ctx;
  c.fillStyle = '#f7f3e6'; c.fillRect(0, 0, innerWidth, innerHeight);
  const sets: { name: string; eq: Record<string, string | undefined> }[] = [
    { name: 'inceput', eq: { weapon: 'pencil_sword', chest: 'pupil_tunic', boots: 'sock_boots' } },
    { name: 'mijloc', eq: { weapon: 'marker_axe', offhand: 'staple_kite', chest: 'stapled_plate', head: 'staple_helm', boots: 'eraser_boots', cape: 'ribbon_cape' } },
    { name: 'final', eq: { weapon: 'quill_of_endings', offhand: 'hardcover_shield', chest: 'binding_plate', head: 'margin_crown', boots: 'courier_boots', cape: 'first_draft_cape' } },
  ];
  const builds: ('chibi' | 'heroic')[] = ['chibi', 'heroic'];
  const cw = innerWidth / sets.length;
  builds.forEach((b, row) => {
    setBuild(b);
    const y = 220 + row * 250;
    sets.forEach((set, col) => {
      const x = cw * (col + 0.5);
      c.save(); c.translate(x, y); c.scale(2.1, 2.1);
      drawFigureFor(c, set.eq as never, 'front');
      c.restore();
      if (row === 1) penText(c, set.name, x, 520, { size: 19, align: 'center', color: PENCIL, seed: col });
    });
    penText(c, b === 'chibi' ? '1 — corpul de acum' : '2 — corp eroic', 26, y - 92,
      { size: 21, color: b === 'chibi' ? PENCIL : RED, font: FONT_PEN, seed: row + 3 });
  });
  setBuild(new URLSearchParams(location.search).get('build') === 'chibi' ? 'chibi' : 'heroic');
  penText(c, 'acelasi echipament, doua corpuri', innerWidth / 2, 44, { size: 25, align: 'center', color: INK, font: FONT_PEN, seed: 5 });
}

let gearSheet = false;
let gearWhich: 'tiers' | 'classes' = 'tiers';
(window as unknown as Record<string, unknown>).__inkGear = (on: boolean, which?: string) => { gearSheet = on; gearWhich = (which as 'classes') ?? 'tiers'; };
function drawGearSheet() {
  const c = ctx;
  c.fillStyle = '#f7f3e6'; c.fillRect(0, 0, innerWidth, innerHeight);
  const rows = gearWhich === 'classes' ? CLASS_STEPS : GEAR_STEPS;
  const n = rows.length, cw = innerWidth / n;
  for (let i = 0; i < n; i++) {
    const g = rows[i]!;
    const x = cw * (i + 0.5);
    for (let v = 0; v < 2; v++) {
      const y = 215 + v * 240;
      c.save(); c.translate(x, y); c.scale(1.45, 1.45);
      drawFigureFor(c, g.eq as never, v === 0 ? 'front' : 'side');
      c.restore();
    }
    penText(c, g.name, x, 470, { size: 19, align: 'center', color: PENCIL, seed: i });
  }
  penText(c, gearWhich === 'classes' ? 'what each hand carries' : 'the same character, as the gear improves', innerWidth / 2, 40, { size: 24, align: 'center', color: INK, font: FONT_PEN, seed: 5 });
}

let walkSheet: false | 'walk' | 'swing' = false;
(window as unknown as Record<string, unknown>).__inkWalk = (which: string | false) => { walkSheet = (which as 'walk') || false; };
function drawWalkSheet() {
  const c = ctx;
  c.fillStyle = '#f7f3e6'; c.fillRect(0, 0, innerWidth, innerHeight);
  const eq = { weapon: 'ruler_blade', offhand: 'staple_kite', chest: 'stapled_plate', head: 'staple_helm', boots: 'eraser_boots', cape: 'ribbon_cape' };
  const n = 8, cw = innerWidth / n;
  for (let i = 0; i < n; i++) {
    for (let v = 0; v < 2; v++) {
      c.save(); c.translate(cw * (i + 0.5), 170 + v * 190); c.scale(1.6, 1.6);
      if (walkSheet === 'swing') drawWalkFrame(c, eq as never, 0, v === 0 ? 'front' : 'side', i / 7);
      else drawWalkFrame(c, eq as never, i, v === 0 ? 'front' : 'side');
      c.restore();
    }
    penText(c, String(i), cw * (i + 0.5), 400, { size: 15, align: 'center', color: PENCIL, seed: i });
  }
  penText(c, walkSheet === 'swing' ? 'the swing, frame by frame' : 'the walk, frame by frame', innerWidth / 2, 40, { size: 22, align: 'center', color: INK, font: FONT_PEN, seed: 5 });
}

let lootSheet = false;
(window as unknown as Record<string, unknown>).__inkLoot = (on: boolean) => { lootSheet = on; };
function drawLootSheet() {
  const c = ctx;
  c.fillStyle = '#f7f3e6'; c.fillRect(0, 0, innerWidth, innerHeight);
  const ids = ['scrap_paper', 'sock_boots', 'ruler_blade', 'stapled_plate', 'binding_plate', 'quill_of_endings'];
  const cw = innerWidth / ids.length;
  for (let i = 0; i < ids.length; i++) {
    c.save(); c.translate(cw * (i + 0.5), 190); c.scale(1.9, 1.9);
    drawLoot(c, { id: 'l' + i, kind: 'loot', def: ids[i], x: 0, y: 0, a: 0, v: 0 } as never, state.t);
    c.restore();
  }
}

let beastSheet = false;
(window as unknown as Record<string, unknown>).__inkBeasts = (on: boolean) => { beastSheet = on; };
function drawBeastSheet() {
  const c = ctx;
  c.fillStyle = '#f7f3e6'; c.fillRect(0, 0, innerWidth, innerHeight);
  const defs = ['scribble_rat', 'ink_slime', 'paper_bat'];
  const cw = innerWidth / defs.length;
  for (let i = 0; i < defs.length; i++) {
    const x = cw * (i + 0.5);
    for (let v = 0; v < 2; v++) {
      c.save(); c.translate(x, 170 + v * 150); c.scale(2.2, 2.2);
      drawEnemy(c, { id: 'b' + i + v, kind: 'enemy', def: defs[i], x: 0, y: 0, a: 0, v: 0, hp: 10, hpMax: 10 } as never, v ? 1.2 : 0.2, v ? 0.5 : 0, false);
      c.restore();
    }
    penText(c, defs[i]!.replace('_', ' '), x, 360, { size: 18, align: 'center', color: PENCIL, seed: i });
  }
}

let trace: [number, number, number][] | null = null;
(window as unknown as Record<string, unknown>).__inkTrace = (on: boolean) => { trace = on ? [] : null; return true; };
(window as unknown as Record<string, unknown>).__inkTraceRead = () => trace ?? [];

let sheetKind: string | null = null;
(window as unknown as Record<string, unknown>).__inkSheet = (kind: string | null) => { sheetKind = kind; };
function drawSheet(kind: string) {
  const c = ctx;
  c.fillStyle = '#f7f3e6'; c.fillRect(0, 0, innerWidth, innerHeight);
  const n = 10, cw = innerWidth / n;
  for (let i = 0; i < n; i++) {
    const w = kind === 'tree' ? 210 : 130, h = kind === 'tree' ? 265 : 105;
    const x = i * cw + (cw - w) / 2, y = 90;
    drawProp(c, { kind: kind as never, x, y, w, h, seed: i, solid: false }, 0);
    penText(c, String(i), x + w / 2, y + h + 30, { size: 16, align: 'center', color: PENCIL, seed: i });
  }
}
// Does any prop draw right up to the edge of its baked canvas? That edge is where art gets cut off.
(window as unknown as Record<string, unknown>).__inkClip = () => {
  const out: Record<string, number | string> = {};
  const kinds: ZoneProp['kind'][] = ['tree', 'hut', 'rock', 'bush', 'stall', 'stump', 'barrel', 'crate', 'sign', 'fence', 'bridge', 'flowers'];
  const sizes: Record<string, [number, number]> = { tree: [210, 265], hut: [300, 240], stall: [280, 220], bridge: [380, 240], fence: [840, 32] };
  for (const kind of kinds) {
    for (let seed = 0; seed < 10; seed++) {
      const [w, h] = sizes[kind] ?? [130, 105];
      const prop = { kind, x: 0, y: 0, w, h, seed, solid: false } as ZoneProp;
      const pad = propPad(prop);
      const cv = document.createElement('canvas');
      cv.width = Math.ceil(w + pad.l + pad.r); cv.height = Math.ceil(h + pad.t + pad.b);
      const c = cv.getContext('2d');
      if (!c) continue;
      c.translate(pad.l, pad.t);
      drawProp(c, prop, 0);
      const d = c.getImageData(0, 0, cv.width, cv.height).data;
      const at = (x: number, y: number) => d[(y * cv.width + x) * 4 + 3]! > 12;
      let top = 0, bottom = 0, left = 0, right = 0;
      for (let x = 0; x < cv.width; x++) { if (at(x, 0)) top++; if (at(x, cv.height - 1)) bottom++; }
      for (let y = 0; y < cv.height; y++) { if (at(0, y)) left++; if (at(cv.width - 1, y)) right++; }
      const total = top + bottom + left + right;
      if (total > 2) out[`${kind}:${seed}`] = `T${top} B${bottom} L${left} R${right}` as unknown as number;
    }
  }
  return out;
};
(window as unknown as InkDebug).__inkFigure = (eq: unknown, view: unknown) => figureInk(eq as never, view as never);
(window as unknown as Record<string, unknown>).__inkBounds = (eq: unknown) => figureBounds(eq as never);
(window as unknown as Record<string, unknown>).__inkHeroBox = (eq: unknown) => heroFrameBox(eq as never);
// Render one swing frame into a canvas the size of its baked frame, outline that frame in red, and
// return a PNG. If any ink touches the red border the weapon is being cut off. QA / clip debugging.
(window as unknown as Record<string, unknown>).__inkSwingShot = (eq: unknown, swing = 1, view = 'side') => {
  const box = heroFrameBox(eq as never);
  const S = 3;
  const cv = document.createElement('canvas');
  cv.width = Math.ceil(box.w * S); cv.height = Math.ceil(box.h * S);
  const c = cv.getContext('2d');
  if (!c) return '';
  c.scale(S, S); c.translate(-box.x, -box.y);
  drawWalkFrame(c, eq as never, 8, view as never, swing as number);
  c.strokeStyle = 'rgba(200,48,47,0.9)'; c.lineWidth = 0.8;
  c.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
  return cv.toDataURL('image/png');
};
(window as unknown as InkDebug).__inkBagHits = () => satchelHits;
(window as unknown as Record<string, unknown>).__inkHeroHits = () => heroHits;
(window as unknown as InkDebug).__inkCam = (x: number, y: number) => { camLock = { x, y }; };
(window as unknown as InkDebug).__ink = () => ({
  me: net.view().find(e => e.id === state.you),
  // where your own figure is actually drawn: the predicted position, not the server's
  pred: pred.ready ? { x: +pred.x.toFixed(2), y: +pred.y.toFixed(2), v: +pred.v.toFixed(1) } : null,
  char: state.char,
  players: net.view().filter(e => e.kind === 'player').length,
  enemies: net.view().filter(e => e.kind === 'enemy').length,
  quests: state.char?.quests,
  ping: Math.round(net.ping),
  reach: (() => { const cl = state.char ? CLASSES[state.char.cls] : undefined; return Math.round(Math.max(40, (cl?.range ?? 46) * 0.82)); })(),
  targetDist: (() => {
    const me2 = selfView(); const t2 = net.view().find(e => e.id === state.target);
    return me2 && t2 ? +Math.hypot(t2.x - me2.x, t2.y - me2.y).toFixed(1) : null;
  })(),
  sent: net.sent,
  frames: frameCount,
});

// The scenery never changes, so each prop is drawn once into its own little canvas and copied to
// the screen after that. This is the difference between a few hundred strokes a frame and a few
// thousand, which is what a phone actually feels.
// How far a drawing reaches outside its own box, per side: a roof overhangs, a crown spills
// upwards, a shadow falls below. Too little here and the art is quietly cut off at the edge of its
// baked canvas; scripts/check-clip.mjs is what keeps these honest.
type Pad = { t: number; r: number; b: number; l: number };
const PAD: Partial<Record<ZoneProp['kind'], [number, number, number, number]>> = {
  //          top   right  bottom  left    (as a fraction of the larger side)
  tree:      [0.62, 0.34, 0.24, 0.34],
  hut:       [0.56, 0.44, 0.4, 0.28],
  stall:     [0.34, 0.24, 0.28, 0.2],
  bridge:    [0.24, 0.1, 0.28, 0.1],
  rock:      [0.2, 0.28, 0.46, 0.4],
  bush:      [0.22, 0.24, 0.46, 0.24],
  stump:     [0.26, 0.3, 0.5, 0.34],
  barrel:    [0.3, 0.34, 0.55, 0.4],
  crate:     [0.3, 0.3, 0.5, 0.36],
  sign:      [0.5, 0.4, 0.3, 0.4],
  flowers:   [0.5, 0.3, 0.45, 0.3],
  fence:     [1.6, 0.2, 1.1, 0.2],
};
function propPad(p: ZoneProp): Pad {
  const f = PAD[p.kind] ?? [0.3, 0.3, 0.4, 0.3];
  const base = p.kind === 'fence' ? Math.min(p.w, p.h) : Math.max(p.w, p.h);
  const px = (v: number) => Math.max(14, Math.round(base * v));
  return { t: px(f[0]), r: px(f[1]), b: px(f[2]), l: px(f[3]) };
}

// Baking is expensive; doing several in one frame is what produces a 300 ms hitch as you walk into
// new scenery. Only a couple are allowed per frame, and anything else is simply drawn straight to
// the screen this time round — it looks the same, it just is not saved yet.
let bakeBudget = 2;
function drawPropCached(p: ZoneProp, t: number, s: number, view: Box) {
  const pad = propPad(p);
  const key = `p|${p.kind}|${p.seed}|${p.w}x${p.h}|${p.text ?? ''}`;
  const have = baked(key, s);
  if (!have && bakeBudget <= 0) { drawProp(ctx, p, t); return; }
  const b = have ?? (bakeBudget--, bake(key, { x: p.x - pad.l, y: p.y - pad.t, w: p.w + pad.l + pad.r, h: p.h + pad.t + pad.b }, s, (c) => drawProp(c, p, 0)));
  if (b) blitVisible(b, view, s);
  else drawProp(ctx, p, t);
  if (p.kind === 'water') drawProp(ctx, p, t, true);
}

/**
 * Copy only the part of a baked image that is actually on screen. The river is six hundred units
 * long and you can see a fifth of it; paying for the whole canvas every frame is most of the cost
 * of drawing a page this dense.
 */
function blitVisible(b: Baked, view: Box, s: number) {
  const x0 = Math.max(b.ox, view.x), y0 = Math.max(b.oy, view.y);
  const x1 = Math.min(b.ox + b.w, view.x + view.w), y1 = Math.min(b.oy + b.h, view.y + view.h);
  if (x1 <= x0 || y1 <= y0) return;
  if (x1 - x0 >= b.w - 0.5 && y1 - y0 >= b.h - 0.5) { ctx.drawImage(b.canvas, b.ox, b.oy, b.w, b.h); return; }
  ctx.drawImage(b.canvas, (x0 - b.ox) * s, (y0 - b.oy) * s, (x1 - x0) * s, (y1 - y0) * s, x0, y0, x1 - x0, y1 - y0);
}

// The ground between landmarks is drawn too: each cell of the world is baked once with its own
// clutter and, sometimes, something scribbled over the page.
/**
 * The world ends because the page ends. Rather than the drawing simply stopping at a straight line,
 * the paper is torn along the boundary and the next page shows underneath.
 */
function drawPageEdge(z: ZoneInfo, camX: number, camY: number, vw: number, vh: number) {
  const sides: [number, number, number, number, number][] = [
    [0, -1, z.w, 0, 0],            // top:    y = 0, outside is above
    [0, 1, z.w, z.h, 1],           // bottom
    [1, -1, z.h, 0, 2],            // left:   x = 0, outside is to the left
    [1, 1, z.h, z.w, 3],           // right
  ];
  for (const [axis, dir, span, at, seed] of sides) {
    // skip an edge that is nowhere near the view
    const near = axis === 0
      ? (dir < 0 ? camY < 90 : camY + vh > z.h - 90)
      : (dir < 0 ? camX < 90 : camX + vw > z.w - 90);
    if (!near) continue;
    const from = axis === 0 ? Math.max(0, camX - 60) : Math.max(0, camY - 60);
    const to = axis === 0 ? Math.min(span, camX + vw + 60) : Math.min(span, camY + vh + 60);
    const steps = Math.max(4, Math.round((to - from) / 42));
    const pts: [number, number][] = [];
    for (let i = 0; i <= steps; i++) {
      const t = from + ((to - from) * i) / steps;
      // a torn edge is uneven at two scales: long bites out of the page and small nicks in them
      const big = (hashStr(`E${seed}${i >> 1}`) % 100) / 100;
      const small = (hashStr(`e${seed}${i}`) % 100) / 100;
      const jag = big * 22 + small * 7 - 5;
      const off = at + dir * jag;
      pts.push(axis === 0 ? [t, off] : [off, t]);
    }
    // the gap beyond the tear, with the next page showing through
    ctx.save();
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    const far = at + dir * 400;
    if (axis === 0) { ctx.lineTo(to, far); ctx.lineTo(from, far); } else { ctx.lineTo(far, to); ctx.lineTo(far, from); }
    ctx.closePath();
    ctx.fillStyle = '#e2dac6'; ctx.fill();
    ctx.globalAlpha = 0.5; ctx.fillStyle = '#cfc6ae';
    ctx.fill();
    ctx.restore();
    inkPath(ctx, pts, { color: PENCIL_SOFT, width: 1.6, seed: seed + 5, amp: 1.2, alpha: 0.7 });
  }
}

// Scenery that lies on the ground is baked into the page itself: it never needs to be drawn in
// front of anything, so it costs nothing per frame beyond the ground it is already part of.
const GROUND_PROPS = new Set(['flowers', 'bush', 'stump', 'grass']);

/**
 * The ground — page texture, worn paths, clutter and the scenery that lies flat — is composed once
 * into a single canvas a little larger than the screen, and that one canvas is copied each frame.
 * It used to be six separate tile copies per frame; on a phone every extra source canvas is a
 * texture bind, and that is what the stutter was made of.
 */
const ground: {
  cv: HTMLCanvasElement | null; x: number; y: number; w: number; h: number; s: number; ok: boolean;
} = { cv: null, x: 0, y: 0, w: 0, h: 0, s: 0, ok: false };
const GROUND_MARGIN = 180;

function drawScatter(camX: number, camY: number, vw: number, vh: number, zoom: number, s: number) {
  const z = state.zone;
  if (!z) return;
  const viewW = vw / zoom, viewH = vh / zoom;
  const stale = !ground.cv || !ground.ok || ground.s !== s
    || camX < ground.x + 8 || camY < ground.y + 8
    || camX + viewW > ground.x + ground.w - 8 || camY + viewH > ground.y + ground.h - 8
    || Math.abs(ground.w - (viewW + GROUND_MARGIN * 2)) > 2;
  if (stale) rebuildGround(camX, camY, viewW, viewH, s);
  if (ground.cv) ctx.drawImage(ground.cv, ground.x, ground.y, ground.w, ground.h);
}

function rebuildGround(camX: number, camY: number, viewW: number, viewH: number, s: number) {
  const z = state.zone;
  if (!z) return;
  const w = viewW + GROUND_MARGIN * 2, h = viewH + GROUND_MARGIN * 2;
  const x = camX - GROUND_MARGIN, y = camY - GROUND_MARGIN;
  const cv = ground.cv ?? document.createElement('canvas');
  const pw = Math.ceil(w * s), ph = Math.ceil(h * s);
  if (cv.width !== pw || cv.height !== ph) { cv.width = pw; cv.height = ph; }
  const c = cv.getContext('2d');
  if (!c) return;
  c.setTransform(s, 0, 0, s, 0, 0);
  c.clearRect(0, 0, w, h);
  c.translate(-x, -y);

  let complete = true;
  const x0 = Math.floor(x / clutterCell), x1 = Math.floor((x + w) / clutterCell);
  const y0 = Math.floor(y / clutterCell), y1 = Math.floor((y + h) / clutterCell);
  // one new tile is drawn per rebuild; the rest come from the cache. A tile that is not ready yet
  // leaves the layer incomplete, so it is rebuilt again next frame rather than left with a hole.
  let budget = 1;
  for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) {
    if (cx < 0 || cy < 0 || cx * clutterCell > z.w || cy * clutterCell > z.h) continue;
    const box = { x: cx * clutterCell, y: cy * clutterCell, w: clutterCell, h: clutterCell };
    const have = baked(`cell|${cx}|${cy}`, s);
    if (!have && budget-- <= 0) { complete = false; continue; }
    const tile = have ?? bake(`cell|${cx}|${cy}`, box, s, (cc) => { drawClutterCell(cc, cx, cy); drawDoodleCell(cc, cx, cy); });
    if (tile) c.drawImage(tile.canvas, box.x, box.y, clutterCell, clutterCell);
    else complete = false;
  }
  // the scenery that lies on the ground belongs to this layer too
  for (const p of z.props) {
    if (!GROUND_PROPS.has(p.kind)) continue;
    if (p.x + p.w < x || p.x > x + w || p.y + p.h < y || p.y > y + h) continue;
    drawProp(c, p, 0);
  }
  ground.cv = cv; ground.x = x; ground.y = y; ground.w = w; ground.h = h; ground.s = s; ground.ok = complete;
}

// ---------- camera ----------
let zoom = 1;
// If a device cannot keep up, the picture is quietly rendered at a lower resolution rather than
// letting the whole game stutter. It climbs back up when there is room.
let renderScale = 1;
// How sharp the page is drawn. A phone screen at 3x costs nine times the pixels of a 1x one for a
// difference almost nobody can see at arm's length, and those pixels are what a weaker phone runs
// out of first. 2.5 is still crisp; the performance setting drops it further.
let dprCap = 2.5;
export const setDprCap = (v: number) => { dprCap = v; };
const maxDpr = () => Math.min(devicePixelRatio || 1, isTouch ? dprCap : 3);
// Baked art is keyed by the scale it was drawn at, and deliberately does NOT follow renderScale:
// dropping the resolution must not re-draw the whole page, or a slow frame makes the next one
// slower still. The art is drawn once at the screen's own scale and simply blitted smaller.
const bakeScale = () => Math.round(maxDpr() * zoom * 2) / 2;
const adapt = { t: 0, low: 0, high: 0 };
let lastVis = 0;
let propView: Box = { x: 0, y: 0, w: 0, h: 0 };
let propScaleNow = 2;
function governQuality(now: number) {
  if (profN.t === 0 || now - adapt.t < 2000) return;
  adapt.t = now;
  const fps = profN.fps;
  if (fps > 0 && fps < 40 && renderScale > 0.65) {
    if (++adapt.low >= 3) { adapt.low = 0; renderScale = Math.max(0.65, renderScale - 0.175); resize(); }
  } else if (fps > 58 && renderScale < 1) {
    if (++adapt.high >= 5) { adapt.high = 0; renderScale = Math.min(1, renderScale + 0.175); resize(); }
  } else { adapt.low = 0; adapt.high = 0; }
}

function resize() {
  // Integer sizes only. A fractional innerWidth (Safari does this, especially with page zoom on)
  // leaves the backing store slightly out of step with the CSS size and the browser rescales the
  // whole canvas — which is exactly what "everything looks blurry" looks like.
  const dpr = maxDpr() * renderScale;
  const w = Math.round(innerWidth), h = Math.round(innerHeight);
  canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
  canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  // phones get a wider view of the page, not a bigger character
  // The camera sits back far enough that a cottage is three times the height of a person, the way
  // an illustrated page frames a village. Any closer and the world reads as a model, not a place.
  zoom = innerWidth < 500 ? 0.62 : innerWidth < 760 ? 0.72 : innerWidth < 1100 ? 0.9 : 1.05;
  setInkScale(zoom);
  setSpriteScale(bakeScale());
}
addEventListener('resize', resize); resize();
const screenToWorld = (sx: number, sy: number) => ({ x: (sx - innerWidth / 2) / zoom + state.cam.x, y: (sy - innerHeight / 2) / zoom + state.cam.y });

// ---------- loop ----------
let last = performance.now();
let inputAcc = 0;
let frameCount = 0;
let autoSwing = 0;
function frame(now: number) {
  frameCount++;
  // A frame that takes 300 ms still covers 300 ms of walking. Clamping this to 50 ms was making
  // the character visibly stall every time the page baked new scenery.
  const dt = Math.min(0.25, (now - last) / 1000); last = now;
  state.t += dt;
  setInkFrame(state.t);
  if (buildSheet) { drawBuildSheet(); requestAnimationFrame(frame); return; }
  if (walkSheet) { drawWalkSheet(); requestAnimationFrame(frame); return; }
  if (lootSheet) { drawLootSheet(); requestAnimationFrame(frame); return; }
  if (beastSheet) { drawBeastSheet(); requestAnimationFrame(frame); return; }
  if (gearSheet) { drawGearSheet(); requestAnimationFrame(frame); return; }
  if (sheetKind) { drawSheet(sheetKind); requestAnimationFrame(frame); return; }

  // movement intent at a fixed rate
  let dx = 0, dy = 0;
  if (joy.on && (Math.abs(joy.x) > 0.12 || Math.abs(joy.y) > 0.12)) {
    dx = joy.x; dy = joy.y; autoGoto = null; pending = null;
  }
  if (!state.chatting) {
    if (keys.has('a') || keys.has('arrowleft')) dx -= 1;
    if (keys.has('d') || keys.has('arrowright')) dx += 1;
    if (keys.has('w') || keys.has('arrowup')) dy -= 1;
    if (keys.has('s') || keys.has('arrowdown')) dy += 1;
  }
  const ents = net.view();
  const server = ents.find(e => e.id === state.you);
  const me = server ? predictSelf(server, dx, dy, dt) : undefined;
  // QA: a record of where the figure actually was on each frame, to measure whether the walk is
  // even. Sampling this from outside the loop cannot work — the sampler and the frame race.
  if (trace && me) { trace.push([now, me.x, me.y]); if (trace.length > 900) trace.shift(); }
  if (pending && me) {
    const target = ents.find(e => e.id === pending!.id);
    if (!target || target.dead) { pending = null; autoGoto = null; }
    else {
      autoGoto = { x: target.x, y: target.y };
      const d = Math.hypot(target.x - me.x, target.y - me.y);
      // Stop at the range your class actually fights at. A mage with 210 units of reach used to
      // walk right up and hit the rat over the head with his staff.
      const cls = state.char ? CLASSES[state.char.cls] : undefined;
      const reach = pending.kind === 'attack' ? Math.max(40, (cls?.range ?? 46) * 0.82) : 70;
      if (d < reach) {
        autoGoto = null;
        // one swing here, then the auto-swing timer takes over: otherwise this fires every frame
        if (pending.kind === 'attack') attack();
        else net.send(pending.kind === 'interact' ? { t: 'interact', target: pending.id } : { t: 'pickup', target: pending.id });
        pending = null;
      }
    }
  }
  if (autoGoto && me) {
    const d = Math.hypot(autoGoto.x - me.x, autoGoto.y - me.y);
    if (d < 24) autoGoto = null;
    else { dx = (autoGoto.x - me.x) / d; dy = (autoGoto.y - me.y) / d; }
  }
  // keep swinging at the thing you picked, at the pace the server allows
  if (me && state.target) {
    const t2 = ents.find(e => e.id === state.target);
    if (!t2 || t2.dead) { state.target = null; pending = null; }
    else if (Math.hypot(t2.x - me.x, t2.y - me.y) < 200) {
      autoSwing -= dt;
      if (autoSwing <= 0) { autoSwing = 0.5; attack(); }
    }
  }
  if (marker) { marker.t -= dt; if (marker.t <= 0) marker = null; }
  inputAcc += dt;
  if (inputAcc > 0.05) { inputAcc = 0; net.input(dx, dy, keys.has('shift') || Math.hypot(joy.x, joy.y) > 0.85); }
  const focus = camLock ?? me;
  if (focus) { state.cam.x += (focus.x - state.cam.x) * Math.min(1, dt * 6); state.cam.y += (focus.y - state.cam.y) * Math.min(1, dt * 6); }

  // swing timers, from the server's act tick
  for (const e of ents) {
    if (e.act !== undefined && state.lastAct.get(e.id) !== e.act) { state.lastAct.set(e.id, e.act); state.swing.set(e.id, 0.28); }
  }
  for (const [id, v] of state.swing) { const n = v - dt; n <= 0 ? state.swing.delete(id) : state.swing.set(id, n); }
  for (let i = shots.length - 1; i >= 0; i--) { shots[i]!.t += dt; if (shots[i]!.t >= shots[i]!.life) shots.splice(i, 1); }
  for (let i = floats.length - 1; i >= 0; i--) {
    const f = floats[i]!;
    f.t += dt; f.y -= dt * 30; f.x += f.vx * dt;
    if (f.t >= f.life) floats.splice(i, 1);
  }

  draw(ents, me);
  governQuality(now);
  requestAnimationFrame(frame);
}

// a tiny profiler: ?debug=1 keeps a running average of where the frame goes
const prof: Record<string, number> = {};
const profN = { n: 0, fps: 0, t: 0, frames: 0 };
const mark = (label: string, t0: number) => { prof[label] = (prof[label] ?? 0) + (performance.now() - t0); };

function draw(ents: EntityState[], me: EntityState | undefined) {
  profN.n++; profN.frames++;
  const nowMs = performance.now();
  if (nowMs - profN.t > 500) { profN.fps = (profN.frames * 1000) / (nowMs - profN.t); profN.t = nowMs; profN.frames = 0; }
  const vw = innerWidth, vh = innerHeight;
  const camX = state.cam.x - vw / 2 / zoom, camY = state.cam.y - vh / 2 / zoom;
  let t0 = performance.now();
  if (OFF.has('paper')) { ctx.fillStyle = '#f7f3e6'; ctx.fillRect(0, 0, vw, vh); } else drawPaperFast(ctx, camX, camY, vw, vh, zoom, maxDpr() * renderScale);
  mark('paper', t0);
  ctx.save();
  ctx.scale(zoom, zoom);
  ctx.translate(-camX, -camY);

  const z = state.zone;
  if (z) {
    // painter order by the bottom edge, so a crate in front of a house overlaps it correctly
    const vis = z.props.filter(p => !GROUND_PROPS.has(p.kind)
      && !(p.x + p.w < camX - 80 || p.y + p.h < camY - 80 || p.x > camX + vw / zoom + 80 || p.y > camY + vh / zoom + 80));
    vis.sort((a, b) => (a.y + a.h) - (b.y + b.h));
    lastVis = vis.length;
    const s = bakeScale();
    t0 = performance.now();
    if (!OFF.has('props')) {
      drawScatter(camX, camY, vw, vh, zoom, Math.min(s, 1.5));
      mark('scatter', t0);
      t0 = performance.now();
      const view: Box = { x: camX, y: camY, w: vw / zoom, h: vh / zoom };
      bakeBudget = 2;
      propView = view; propScaleNow = s;
    }
  }

  // Scenery and figures are drawn as one list, in order of where their feet are. Drawing all the
  // scenery and then all the figures put the character on top of the roof whenever he stood north
  // of a house.
  t0 = performance.now();
  const sorted = OFF.has('ents') ? [] : [...ents].sort((a, b) => a.y - b.y);
  if (z && !OFF.has('props')) {
    const layer: { k: number; p?: ZoneProp; e?: EntityState }[] = [];
    for (const p of z.props) {
      if (GROUND_PROPS.has(p.kind)) continue;
      if (p.x + p.w < camX - 80 || p.y + p.h < camY - 80 || p.x > camX + vw / zoom + 80 || p.y > camY + vh / zoom + 80) continue;
      layer.push({ k: p.y + p.h, p });
    }
    for (const e of sorted) layer.push({ k: e.y, e });
    layer.sort((a, b) => a.k - b.k);
    // props and figures are interleaved now, so they are timed separately rather than as one blob
    for (const it of layer) {
      const t1 = performance.now();
      if (it.p) { drawPropCached(it.p, state.t, propScaleNow, propView); mark('props', t1); continue; }
      drawEntity(it.e!, me); mark('ents', t1);
    }
    // the tear goes over everything: the world stops where the paper does
    drawPageEdge(z, camX, camY, vw / zoom, vh / zoom);
  } else for (const e of sorted) drawEntity(e, me);
  mark('world', t0);

  function drawEntity(e: EntityState, me2: EntityState | undefined) {
    const sw = state.swing.get(e.id) ?? 0;
    if (e.kind === 'player') drawHero(ctx, e, state.t, e.id === state.you, sw / 0.28, unpackEquip(e.eq));
    else if (e.kind === 'enemy') {
      const near = me2 ? Math.hypot(e.x - me2.x, e.y - me2.y) < 78 : false;
      // whatever you are fighting gets a ring drawn round its feet, the way you would circle it
      if (e.id === state.target) {
        const r = 1 + Math.sin(state.t * 3) * 0.04;
        inkEllipse(ctx, e.x, e.y + 6, 26 * r, 11 * r, { color: RED, width: 1.5, amp: 1.1, alpha: 0.85, seed: 771, dash: [7, 5] });
      }
      drawEnemy(ctx, e, state.t, sw / 0.28, !near && e.id !== state.target);
    }
    else if (e.kind === 'npc') drawNpcFigure(ctx, e, state.t, false);
    else if (e.kind === 'loot') drawLoot(ctx, e, state.t);
  }

  // where you tapped
  if (marker) {
    const k = Math.min(1, marker.t * 2.2);
    inkLine(ctx, marker.x - 8, marker.y - 8, marker.x + 8, marker.y + 8, { color: RED, width: 2, amp: 1.2, seed: 501, alpha: k });
    inkLine(ctx, marker.x + 8, marker.y - 8, marker.x - 8, marker.y + 8, { color: RED, width: 2, amp: 1.2, seed: 502, alpha: k });
  }
  // quest markers sit above every prop and figure
  for (const e of sorted) if (e.kind === 'npc' && questOpen(e.def)) drawQuestMark(ctx, e.x, e.y, state.t);
  // target ring
  if (state.target) {
    const t = ents.find(e => e.id === state.target);
    if (t) inkEllipse(ctx, t.x, t.y, 22, 12, { color: RED, width: 1.6, amp: 2, seed: 777, alpha: 0.7 });
    else state.target = null;
  }

  for (const sh of shots) drawShot(ctx, sh);
  for (const f of floats) drawFloat(ctx, f);
  ctx.restore();

  t0 = performance.now();
  if (!OFF.has('hud')) drawHud(me);
  mark('hud', t0);
  if (joy.on) {
    inkEllipse(ctx, joy.ox, joy.oy, JOY_R, JOY_R, { color: INK, width: 2, amp: 2.2, seed: 811, alpha: 0.45 });
    inkEllipse(ctx, joy.ox + joy.x * JOY_R, joy.oy + joy.y * JOY_R, 20, 20, { color: RED, width: 2.4, amp: 1.8, seed: 812, alpha: 0.75 });
  }
}

/** run the server's movement rules locally, then pull gently towards what the server reported */
/** an arrow or a bolt of ink crossing the page */
function drawShot(ctx: CanvasRenderingContext2D, sh: Shot) {
  const k = Math.min(1, sh.t / sh.life);
  const x = sh.x0 + (sh.x1 - sh.x0) * k, y = sh.y0 + (sh.y1 - sh.y0) * k;
  const a = Math.atan2(sh.y1 - sh.y0, sh.x1 - sh.x0);
  const tailX = x - Math.cos(a) * 16, tailY = y - Math.sin(a) * 16;
  if (sh.kind === 'arrow') {
    inkLine(ctx, tailX, tailY, x, y, { color: PENCIL, width: 1.6, amp: 0.4, seed: 900, alpha: 0.95 });
    inkLine(ctx, x, y, x - Math.cos(a - 0.5) * 5, y - Math.sin(a - 0.5) * 5, { color: PENCIL, width: 1.4, amp: 0.3, seed: 901 });
    inkLine(ctx, x, y, x - Math.cos(a + 0.5) * 5, y - Math.sin(a + 0.5) * 5, { color: PENCIL, width: 1.4, amp: 0.3, seed: 902 });
  } else {
    inkLine(ctx, tailX, tailY, x, y, { color: INK, width: 2.4, amp: 0.8, seed: 903, alpha: 0.7 });
    inkBlot(ctx, x, y, 3.2, INK, 904, 0.85);
  }
}

function predictSelf(server: EntityState, dx: number, dy: number, dt: number): EntityState {
  const c = state.char;
  if (!pred.ready || !c || !state.zone) { pred.x = server.x; pred.y = server.y; pred.a = server.a; pred.ready = !!state.char; return server; }
  const cls = CLASSES[c.cls];
  let bonus = 0;
  for (const id of Object.values(c.equip)) { const it = id ? ITEMS[id] : undefined; if (it?.speed) bonus += it.speed; }
  const running = keys.has('shift') || Math.hypot(joy.x, joy.y) > 0.85;
  const speed = (cls.speed + bonus) * (running ? 1.35 : 1);
  const len = Math.hypot(dx, dy);
  if (server.dead) { pred.x = server.x; pred.y = server.y; pred.v = 0; }
  else if (len > 0.08) {
    const nx = dx / len, ny = dy / len;
    pred.a = Math.atan2(ny, nx);
    pred.v = speed * Math.min(1, len);
    moveCollide(solids, pred, nx * pred.v * dt, ny * pred.v * dt, 16, state.zone.w, state.zone.h);
  } else pred.v = 0;
  // Reconcile. While you are walking, your own figure is always ahead of what the server has sent
  // back — by about one round trip's worth of distance. Correcting inside that distance means
  // pulling the character backwards every frame against its own movement, which is felt as the
  // walk stalling and starting again. So the correction only begins past the expected lead, and
  // the lead is computed from the actual speed and ping rather than being a fixed number of pixels.
  const err = Math.hypot(server.x - pred.x, server.y - pred.y);
  const lead = speed * Math.min(0.4, net.ping / 1000 + 0.07) + 8;
  if (err > Math.max(120, lead * 3.5)) { pred.x = server.x; pred.y = server.y; }
  else if (err > lead) {
    // An exponential rate, capped: `dt * rate` reaches 1 on a long frame and snaps the character
    // all the way back to where the server last saw it, undoing the step it just took.
    const k = Math.min(0.45, 1 - Math.exp(-(err > lead * 2 ? 6 : 2.2) * dt));
    pred.x += (server.x - pred.x) * k; pred.y += (server.y - pred.y) * k;
  }
  return { ...server, x: pred.x, y: pred.y, a: pred.v > 1 ? pred.a : server.a, v: pred.v };
}

function questOpen(npcId: string) {
  const c = state.char; if (!c) return false;
  for (const q of Object.values(QUESTS)) {
    if (q.giver !== npcId) continue;
    const st = c.quests.find(s => s.id === q.id);
    if (!st) return true;
    if (!st.done) return true;
  }
  return false;
}

// ---------- HUD ----------
function drawHud(me: EntityState | undefined) {
  const c = state.char; if (!c) return;
  const narrow = innerWidth < 760;
  const scale = narrow ? 0.8 : 1;

  drawStatus(ctx, c, me, narrow ? 10 : 14, narrow ? 8 : 12, scale);

  // on a phone the quest note goes under the status card, not beside it
  const qw = narrow ? Math.min(250, innerWidth - 20) : 250;
  if (state.questOpen) {
    if (narrow) drawQuestNote(ctx, c, 10, 92, qw);
    else drawQuestNote(ctx, c, innerWidth - qw - 22, 20, qw);
  }
  // the map sits under the quest note on a wide screen, and over the page on a phone
  if (state.mapOpen && state.zone) {
    const mw = narrow ? Math.min(260, innerWidth - 24) : 250;
    const mx = narrow ? (innerWidth - mw) / 2 : innerWidth - mw - 22;
    const my = narrow ? 92 : state.questOpen ? 20 + questNoteHeight(c) + 12 : 20;
    drawMiniMap(ctx, state.zone, me, net.view().filter(e => e.kind === 'npc'), mx, my, mw);
  }

  // whoever you are fighting, shown once, at the top
  if (state.target) {
    const tgt = net.view().find(e => e.id === state.target && !e.dead);
    if (tgt) drawTargetPlate(ctx, tgt, innerWidth / 2, 14);
  }

  const chatBottom = innerHeight - (isTouch ? 116 : 34);
  drawChat(ctx, chatLog, state.t, 20, chatBottom, narrow ? innerWidth - 190 : Math.min(460, innerWidth - 220));

  // the character sheet, when it is open, sits over the middle of the page
  if (state.heroOpen && c) {
    const hb = heroBox();
    heroHits = drawCharacterSheet(ctx, c, hb.x, hb.y, hb.w, hb.h,
      (cc, eq) => drawFigureFor(cc, eq as never, 'front'), isTouch ? undefined : mouse);
  } else heroHits = [];

  const sb = satchelBox();
  satchelHits = state.invOpen ? drawSatchel(ctx, c, sb.x, sb.y, sb.w, sb.h, narrow, isTouch ? undefined : mouse) : [];


  if (DEBUG) {
    const fps = profN.fps ?? 0;
    penText(ctx, `dpr ${maxDpr().toFixed(2)}x${renderScale.toFixed(1)} · canvas ${canvas.width}x${canvas.height} · css ${Math.round(innerWidth)}x${Math.round(innerHeight)} · zoom ${zoom} · ${fps.toFixed(0)} fps · ${spriteCount()} sprites`,
      10, innerHeight - 8, { size: 12, color: RED, seed: 1 });
  }
  if (state.status !== 'online') {
    inkBlot(ctx, innerWidth / 2, 44, 9, RED, 99, 0.45);
    penText(ctx, state.status === 'connecting' ? 'finding the page…' : 'the page is closed', innerWidth / 2, 50, { size: 19, align: 'center', color: RED, font: FONT_PEN, seed: 62 });
  }
  if (me?.dead) {
    penText(ctx, 'scribbled out', innerWidth / 2, innerHeight / 2 - 10, { size: 46, align: 'center', color: RED, font: FONT_PEN, seed: 63, alpha: 0.9 });
    penText(ctx, 'you wake up back in the village', innerWidth / 2, innerHeight / 2 + 18, { size: 16, align: 'center', color: PENCIL, seed: 64 });
  }
}

function heroBox() {
  const narrow = innerWidth < 760;
  const w = Math.min(narrow ? innerWidth - 20 : 560, innerWidth - 20);
  const h = Math.min(narrow ? 400 : 380, innerHeight - (narrow ? 250 : 190));
  return { x: (innerWidth - w) / 2, y: Math.max(narrow ? 56 : 70, (innerHeight - h) / 2 - (narrow ? 70 : 40)), w, h };
}

function satchelBox() {
  // The satchel has to clear the tool tabs along the bottom edge, or its last row of slots sits
  // underneath them and cannot be tapped.
  const narrow = innerWidth < 760;
  if (narrow) {
    const w = innerWidth - 20, h = 282;
    return { x: 10, y: Math.max(96, innerHeight - h - 196), w, h };
  }
  const w = Math.min(430, innerWidth - 24), h = 260;
  return { x: innerWidth - w - 20, y: innerHeight - h - 96, w, h };
}

void hashStr;

// Nothing is drawn until the handwriting is actually available, otherwise the first frames fall
// back to a system sans and the whole page looks like a form.
async function start() {
  try {
    await Promise.race([
      Promise.all([
        (document as Document & { fonts: FontFaceSet }).fonts.load('16px "Patrick Hand"'),
        (document as Document & { fonts: FontFaceSet }).fonts.load('700 28px "Caveat"'),
        (document as Document & { fonts: FontFaceSet }).fonts.ready,
      ]),
      new Promise((r) => setTimeout(r, 3000)),
    ]);
  } catch { /* fall back to the stack in penText */ }
  drawWho();
  addEventListener('resize', drawWho);
  requestAnimationFrame(frame);
}
start();
